import {
  type SocketStatus,
  type StateExternalStore,
} from "@client/ImperativeState";
import { trySetItem } from "@client/utils/storageWrite";
import { isRestrictedFunderViewer } from "@engine/round/accessors";
import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
  useSyncExternalStore,
  type ReactNode,
} from "react";

import {
  ClientSettingsContext,
  getSimulationHorizonFromLocalStorage,
  setSimulationHorizonToLocalStorage,
} from "./clientSettingsContext";
import { StateContext, useStateSelector } from "./round-context";

/**
 * Providers for the round shell (RoundWrapper in Round.tsx).
 *
 * These exist so the shell itself never subscribes to round state: each
 * provider takes `children` from the non-re-rendering shell, so when a
 * provider re-renders on its own narrow subscription (or local state), React
 * bails out of the referentially-stable `children` element and the routed
 * subtree is not parent-rendered. That stable-children seam is what makes the
 * per-field selector hooks in round-context.ts deliver actual render wins —
 * a whole-state subscription in the shell would re-render every mounted round
 * screen from above regardless of what the leaves subscribe to.
 *
 * They live in this leaf module (not Round.tsx) so deep components can import
 * the contexts without dragging in the whole page module (the Linux-CI
 * "Cannot access X before initialization" cycle class), and so the
 * stable-children behavior can be pinned by dom tests without mounting the
 * full round shell.
 */

export const PinnedNodesContext = createContext<{
  pinnedNodes: string[];
  hoveredNode: string | null;
  pinNode: (node: string) => void;
  unpinNode: (node: string) => void;
  togglePinNode: (node: string) => void;
  setHoveredNode: (node: string | null) => void;
}>({
  pinnedNodes: [],
  hoveredNode: null,
  pinNode: () => {},
  unpinNode: () => {},
  togglePinNode: () => {},
  setHoveredNode: () => {},
});

export function useEvaluableControls(
  nodeId: string,
  relatedShortNames?: string[],
) {
  const context = useContext(PinnedNodesContext);

  const isSourceHighlighted =
    !!relatedShortNames &&
    !!context.hoveredNode &&
    relatedShortNames.includes(context.hoveredNode);

  const isSelected = context.pinnedNodes.includes(nodeId);
  const isHovered = context.hoveredNode === nodeId || isSourceHighlighted;
  const isFocus = isSelected || isHovered;

  const hover = () => context.setHoveredNode(nodeId);
  const unhover = () => context.setHoveredNode(null);

  return { isFocus, isHovered, isSelected, hover, unhover };
}

export const SocketStatusContext = createContext<SocketStatus | null>(null);

/**
 * Subscribes to the socket status alone. Status flips (connect/disconnect)
 * re-render this provider, but `children` comes from the inert shell, so the
 * update reaches only SocketStatusContext consumers.
 */
export function SocketStatusProvider({
  stateStore,
  children,
}: {
  stateStore: StateExternalStore;
  children: ReactNode;
}) {
  const getSocketStatus = useCallback(
    () => stateStore.getSnapshot().socketStatus,
    [stateStore],
  );
  const socketStatus = useSyncExternalStore(
    stateStore.subscribe,
    getSocketStatus,
    getSocketStatus,
  );
  return (
    <SocketStatusContext.Provider value={socketStatus}>
      {children}
    </SocketStatusContext.Provider>
  );
}

const AI_FEATURES_KEY = "aiFeaturesEnabled";
const getAiFeaturesFromLocalStorage = (): boolean => {
  const stored = localStorage.getItem(AI_FEATURES_KEY);
  return stored !== null ? stored === "true" : true;
};
const setAiFeaturesInLocalStorage = (value: boolean) =>
  // Best-effort: a quota-full device keeps the stored value rather than
  // throwing out of the settings toggle.
  void trySetItem(localStorage, AI_FEATURES_KEY, String(value));

/**
 * Owns the client-settings local state (simulation horizon, AI features) and
 * the restricted-funder derivation that force-disables AI surfaces. Toggling
 * a setting re-renders this provider and its context consumers — not the
 * whole round subtree, because `children` is stable.
 */
export function ClientSettingsProvider({ children }: { children: ReactNode }) {
  const store = useContext(StateContext);
  const [simulationHorizon, setSimulationHorizonState] = useState(() =>
    getSimulationHorizonFromLocalStorage(store.getSnapshot().round),
  );
  const [aiFeaturesEnabled, setAiFeaturesEnabledState] = useState(
    getAiFeaturesFromLocalStorage,
  );
  // Every AI-assist surface (note scoring, note density, MVF consistency)
  // honors the aiFeaturesEnabled client setting, so forcing it off for
  // restricted funders shuts down all of them at the chokepoint the user
  // toggle already uses — the server denies those routes to restricted
  // funders regardless. Keyed to the acting-as user so an admin's View-As
  // preview matches what the restricted funder sees. Selected as one boolean
  // so ordinary round churn doesn't re-render the provider.
  const viewerIsRestrictedFunder = useStateSelector(
    (state) =>
      state.actingAsUserId != null &&
      isRestrictedFunderViewer(state.round, state.actingAsUserId),
  );

  return (
    <ClientSettingsContext.Provider
      value={useMemo(
        () => ({
          getSimulationHorizon: () => simulationHorizon,
          setSimulationHorizon: (value: number) => {
            setSimulationHorizonState(value);
            setSimulationHorizonToLocalStorage(value);
          },
          aiFeaturesEnabled: aiFeaturesEnabled && !viewerIsRestrictedFunder,
          setAiFeaturesEnabled: (enabled: boolean) => {
            setAiFeaturesEnabledState(enabled);
            setAiFeaturesInLocalStorage(enabled);
          },
        }),
        [simulationHorizon, aiFeaturesEnabled, viewerIsRestrictedFunder],
      )}
    >
      {children}
    </ClientSettingsContext.Provider>
  );
}

/**
 * Owns the pinned/hovered evaluable-node state for the MVF graph and alluvial
 * charts. Hover churn is the hot case: pointer moves over chart nodes update
 * this state constantly, and with the state owned here (stable children)
 * those updates reach only PinnedNodesContext consumers instead of
 * re-rendering the entire round page from the shell.
 */
export function PinnedNodesProvider({ children }: { children: ReactNode }) {
  const [pinnedNodes, setPinnedNodes] = useState<string[]>([]);
  const [hoveredNode, setHoveredNode] = useState<string | null>(null);

  const pinNode = useCallback((node: string) => {
    setPinnedNodes((prev) => [...prev, node]);
  }, []);

  const unpinNode = useCallback((node: string) => {
    setPinnedNodes((prev) => prev.filter((n) => n !== node));
  }, []);

  const togglePinNode = useCallback((node: string) => {
    setPinnedNodes((prev) => {
      if (prev.includes(node)) {
        return prev.filter((n) => n !== node);
      }
      const isRecommender = node.startsWith("rec-");
      // Replace any existing pinned node of the same type, keep the other type
      return [
        ...prev.filter((n) => n.startsWith("rec-") !== isRecommender),
        node,
      ];
    });
  }, []);

  return (
    <PinnedNodesContext.Provider
      value={useMemo(
        () => ({
          pinnedNodes,
          pinNode,
          unpinNode,
          togglePinNode,
          hoveredNode,
          setHoveredNode,
        }),
        [pinnedNodes, pinNode, unpinNode, togglePinNode, hoveredNode],
      )}
    >
      {children}
    </PinnedNodesContext.Provider>
  );
}
